import { CheckCircle2, Lock } from "lucide-react"

import { BadgeHolderCount } from "@/components/badges/badge-holder-count"
import { BadgeImage } from "@/components/badges/badge-image"
import { PremiumBadge } from "@/components/badges/premium-badge"
import { RarityPill } from "@/components/badges/rarity-pill"
import { Progress } from "@/components/ui/progress"
import { type Tier, tierFor } from "@/lib/badge-tiers"
import { cn } from "@/lib/utils"
import type { BadgeRarity } from "@/types/badges"

/**
 * OKS-50 — Single badge tile.
 * OKS-401 — Now wraps a premium tier medallion (`<PremiumBadge>`) in
 * place of the old flat accent icon-circle.
 *
 * Two visual variants:
 *   - `earned` — full gradient tier medallion + glow + checkmark stamp
 *     on the top-right corner (UX Spec §2.2).
 *   - `locked` — desaturated tier silhouette + lock overlay (UX Spec
 *     §2.2/§3; PRD §7 Q6 — locked tiles are visible for milestones,
 *     hidden for unawarded customs).
 *
 * OKS-937 — `ariaLabel` is REQUIRED and is the whole announcement. This
 * component composes no user-facing copy of its own: it has no
 * `useTranslation`, so anything it composed would be untranslated *by
 * construction* — which is exactly how a hardcoded English `— locked`
 * survived three occurrences (OKS-175, OKS-489, OKS-926) in a
 * French-default product.
 *
 * The previous default composition (and the `tierLabel` prop that fed it)
 * is deliberately DELETED rather than translated. Translating it would
 * have entrenched two divergent sources of the same sentence; deleting it
 * means the locked branch cannot recur *because it no longer exists*. The
 * `ariaLabel` type is the guard — a new call site that forgets to
 * announce is a `tsc --noEmit` failure, not a runtime guess.
 *
 * Callers own the sentence, resolved through i18n (see `badges.aria.*`):
 * tier and earned-state are interpolated into the translated string at the
 * call site. NOTE the state word must key off the CALLER's surface, never
 * off `earnedAt` — that prop is overloaded as a styling flag (the brand
 * catalogue passes `""`/`now` for tiles nobody earned), so deriving a
 * state word from it would announce a falsehood to brand admins.
 */
interface BadgeTileProps {
  icon: string
  accentColor: string
  name: string
  /** When supplied, renders an earned tile (with checkmark stamp). */
  earnedAt?: string | null
  /** Short description / locked-hint sub-line under the name. */
  hint?: string
  /**
   * REQUIRED (OKS-937) — the tile's complete accessible name, already
   * translated and interpolated by the caller. Passed straight through:
   * this component appends, prepends and separates nothing.
   */
  ariaLabel: string
  /**
   * Derived tier (OKS-401). When omitted, falls back to deriving from
   * `accentColor` so non-creator surfaces (brand catalogue, award modal)
   * still render a premium medallion; the creator grid/preview pass the
   * exact ladder-derived tier.
   */
  tier?: Tier
  /**
   * Medallion size — `hero` 60 (grid) | `preview` 48 (preview row) |
   * `detail` 124 (OKS-527 §14 detail-sheet medallion).
   */
  size?: "hero" | "preview" | "detail"
  /** Pins the celebratory NEW chip (earned + recent). */
  isNew?: boolean
  /** Already-translated NEW chip label. */
  newLabel?: string
  /**
   * OKS-538 — display primitive. `medallion` (default) keeps the Lucide
   * tier medallion for milestones; `image` renders the frame-less
   * `<BadgeImage>` for custom badges (uploaded image or accent-tinted
   * default).
   */
  variant?: "medallion" | "image"
  /** Custom-badge image URL (variant `image`); `null` ⇒ default tint. */
  imageUrl?: string | null
  /**
   * OKS-527 — locked progress treatment. When supplied, renders a thin
   * bar (`current/threshold`) + `progressCaption` under the name. Omitted
   * for earned tiles and threshold-less locked badges (`top_performer`).
   */
  progress?: { current: number; threshold: number }
  /** OKS-527 — already-composed caption ("12/30 · streaks"). */
  progressCaption?: string
  /**
   * OKS-527 — crew-internal holder count. When defined (incl. 0), renders
   * the `👥 N have this` chip + rarity pill meta row.
   */
  holderCount?: number
  /** OKS-527 — scarcity bucket driving the rarity pill. */
  rarity?: BadgeRarity
  /** Optional onClick (mobile tap → Sheet — wired by parent). */
  onClick?: () => void
  className?: string
}

export function BadgeTile({
  icon,
  accentColor,
  name,
  earnedAt,
  hint,
  ariaLabel,
  tier,
  size = "hero",
  isNew = false,
  newLabel,
  variant = "medallion",
  imageUrl,
  progress,
  progressCaption,
  holderCount,
  rarity,
  onClick,
  className,
}: BadgeTileProps) {
  const earned = earnedAt != null
  const progressPercent =
    progress && progress.threshold > 0
      ? Math.round((progress.current / progress.threshold) * 100)
      : 0
  const resolvedTier = tier ?? tierFor({ kind: "custom", accentColor })

  // OKS-538/573 — pick the display primitive. Custom badges force the
  // frame-less `<BadgeImage>` (`variant="image"`, accent-tinted default
  // when `imageUrl` is null). Milestones default to the Lucide
  // `<PremiumBadge>` medallion, but render the frame-less image when a
  // brand override URL is present (OKS-573) — so an un-customised
  // milestone keeps its shipped medallion.
  const showImage = variant === "image" || imageUrl != null

  const Wrapper = onClick ? "button" : "div"

  return (
    <Wrapper
      type={onClick ? "button" : undefined}
      onClick={onClick}
      // OKS-926 — a non-interactive tile is a plain <div>, and `aria-label`
      // on a roleless generic is not reliably exposed. The NAME is safe
      // either way (it lives in visible text below, and `line-clamp` is
      // CSS-only), but the tier + earned/locked tokens exist ONLY in this
      // label, so without a naming-capable role a screen-reader user hears
      // the badge but not its tier or whether they earned it (UX Spec §6).
      // `group` over `img`: `img` would hide the locked tile's <Progress>.
      role={onClick ? undefined : "group"}
      data-testid="badge-tile"
      data-state={earned ? "earned" : "locked"}
      aria-label={ariaLabel}
      className={cn(
        // OKS-926 — padding tightens below `sm` to buy label width in the
        // 2-up mobile grids; today's `p-4` is kept from `sm` up.
        "bg-card relative flex flex-col items-center gap-2 rounded-lg border p-2 text-center transition sm:p-4",
        onClick && "cursor-pointer hover:scale-105",
        !earned && "opacity-60",
        className,
      )}
    >
      {showImage ? (
        <BadgeImage
          imageUrl={imageUrl}
          accentColor={accentColor}
          name={name}
          state={earned ? "earned" : "locked"}
          size={size}
          isNew={earned && isNew}
          newLabel={newLabel}
          decorative
        />
      ) : (
        <PremiumBadge
          tier={resolvedTier}
          icon={icon}
          state={earned ? "earned" : "locked"}
          size={size}
          isNew={earned && isNew}
          newLabel={newLabel}
        />
      )}

      {earned && (
        <CheckCircle2
          className="absolute top-2 right-2 h-4 w-4 text-emerald-500"
          aria-hidden="true"
          data-testid="badge-tile-earned-stamp"
        />
      )}
      {!earned && (
        <Lock
          className="text-muted-foreground absolute top-2 right-2 h-4 w-4"
          aria-hidden="true"
          data-testid="badge-tile-locked-stamp"
        />
      )}

      {/*
        OKS-926 — the badge name. Two things keep it legible:

        `wrap-anywhere` (`overflow-wrap: anywhere`) is the structural
        guarantee: without it a word wider than the box does not break, it
        overflows and is sliced mid-glyph by the clamp's hidden overflow —
        "Première" rendering as "Premièr". Note this is deliberately NOT
        `break-words`: only `anywhere` also shrinks intrinsic min-content
        width, which is what stops a 60-char space-less custom name from
        widening its grid track instead of wrapping inside it.

        The line budget is inverted on purpose — MORE lines where the box is
        NARROWER (UX Spec §2b/§9.11), because a narrow box needs more lines
        for the same name. A flat budget binds hardest exactly where the box
        is tightest. 4 below `sm` is measured, not chosen: the longest string
        on either surface ("Meilleur créateur — septembre 2026") needs
        exactly 4 lines in a 2-up catalogue tile at 320px.

        PROVENANCE: that "4 lines" was MEASURED in a real browser — natural
        line count with the clamp removed, so the answer could not depend on
        the clamp under test — and the budget held across an 86-106px box
        sweep and a 14-16px font sweep. The guarantee is "true when
        measured, by that instrument", NOT "invariant". Nothing reds if it
        drifts: the E2E asserts the PROPERTY (shipped names fit in full, no
        name sliced), never the number 4, precisely so a wrong budget fails
        instead of agreeing with the comment that justified it. If you
        change padding, grid density or font size, re-measure — do not
        trust this line. See tests/playwright/badge-name-legibility-oks926.

        OKS-972 changed grid density, so that re-measure was owed and was
        done. ⚠️ It is recorded as a SECOND ANCHOR, not as a widened range,
        because the two ends answer different questions under different
        budgets and neither can do the other's work:

          narrow end — 86-106px box under `line-clamp-4`. The 4-line datum
                       above. Governs below `sm`. UNCHANGED by OKS-972.
          wide end   — 133px box @768 under `sm:line-clamp-3`, 0 of 13
                       bounded names clipped (measured 2026-08-19 on the
                       14-family catalogue). Governs the `lg` regime, where
                       OKS-972 moved the boxes to 167px (goal board) and
                       184px (/profile preview) — both wider than 133 under
                       the SAME clamp, so nothing there needs more lines
                       than it needed at 133.

        Writing this as one continuous 86->184px sweep would assert a
        measurement chain that does not exist, and would quietly break the
        argument that produced OKS-972's fix: that argument needs the 4-line
        datum to mean specifically "4 natural lines, clamp removed, in the
        86-106px band". Widen the band in prose and the claim loses its
        subject — in the one comment a future reader will cite.

        The line budget itself is UNTOUCHED by any of this: `line-clamp-4
        sm:line-clamp-3` still says exactly what it always said.

        `-webkit-line-clamp` supplies the trailing ellipsis itself and cuts
        at the LINE box, so words stay whole — the "downwards" cut PRD §3.2
        permits, never the sideways one it forbids.
      */}
      <p
        data-testid="badge-tile-name"
        className="line-clamp-4 max-w-full text-sm font-medium wrap-anywhere sm:line-clamp-3"
      >
        {name}
      </p>
      {hint && earned && (
        <p className="text-muted-foreground line-clamp-1 text-xs">{hint}</p>
      )}

      {/* OKS-527 — locked progress bar + caption. */}
      {progress && (
        <div
          className="flex w-full flex-col gap-1"
          data-testid="badge-tile-progress"
        >
          <Progress
            value={progressPercent}
            aria-label={progressCaption}
            className="h-1.5"
          />
          {progressCaption && (
            <p className="text-muted-foreground line-clamp-1 text-xs">
              {progressCaption}
            </p>
          )}
        </div>
      )}

      {/* OKS-527 — holder-count + rarity meta row. */}
      {holderCount != null && (
        <div className="flex w-full flex-wrap items-center justify-center gap-1.5">
          <BadgeHolderCount count={holderCount} />
          {rarity && <RarityPill rarity={rarity} />}
        </div>
      )}
    </Wrapper>
  )
}
